/**
 * Complexity telemetry — a per-assessment-phrase record of exactly what the
 * runtime did, laid out to line up with `ComplexityOracle.xlsx` so a session can
 * be replayed in the sheet and compared column-for-column.
 *
 * Why record rather than recompute at dump time: the model
 * (`computeTrajectory`) is already validated 33/33 against the oracle, so
 * re-deriving the bands from the final verdict sequence would trivially match
 * itself and prove nothing about the running app. This captures the band the
 * runtime *actually used to request each phrase* (at plan time) and the verdict
 * it *actually graded* (at completion). {@link ComplexityTelemetry.selfCheck}
 * then diffs those live values against a clean `computeTrajectory` of the same
 * verdicts — any mismatch is a runtime bug (PAD threading, ordering, rest
 * interleaving), not a model error.
 *
 * This is a testing/diagnostic aid, not production behaviour. Nothing here
 * affects phrase selection.
 */

import { computeTrajectory } from './complexityModel';
import { TierConfig, Verdict } from './complexityModel.types';
import { NwcsBand } from './SessionComplexityMap';

/** One assessment phrase, filled across plan time and grade time. */
export interface PhaseRecord {
  /** 1-based assessment index — the oracle's Phrase # (column A). */
  idx: number;
  /** Verdict earned at this phrase — the oracle's Status (column B). `null`
   *  until the phrase has been graded. */
  verdict: Verdict | null;
  /** The verdict actually fed to the recurrence — equals {@link verdict} except
   *  when a below-band Green was held down to Yellow (see
   *  {@link AssessmentStream.grade}). This, not {@link verdict}, is what stepped
   *  the band, so `selfCheck` recomputes the trajectory from it; the diagnostic
   *  table shows both so a held Green reads as `verdict=G / modelVerdict=Y`.
   *  `null` until graded. */
  modelVerdict: Verdict | null;
  /** Weighted error tally that produced the verdict (red 1.0 + yellow 0.5). */
  errorTotal: number | null;
  redNotes: number | null;
  yellowNotes: number | null;
  /** Position the runtime was at for this phrase — the recurrence's tracked
   *  scalar, and the oracle's column C. Captured at plan time.
   *
   *  Under the sheet's floor anchoring this equals `crMin`; under the shipping
   *  centre anchoring it is the band's midpoint. Column C of the sheet is this
   *  value, so this is what a live oracle comparison must diff. */
  position: number;
  /** Band floor the runtime used to request this phrase. Derived from
   *  `position` via the tier's anchoring. */
  crMin: number;
  /** Band ceiling — the oracle's CR Max (column D). */
  crMax: number;
  /** PAD-delayed driver: verdict[idx - pad], the verdict that moved this band. */
  driver: Verdict | undefined;
  /** The oracle's PLATEAU value (anti-jitter cooldown) for this row. */
  cooldown: number;
  /** Rest phrases injected before this assessment phrase. */
  restsBefore: number;
  /** Live accuracy % at grade time. `null` until graded. */
  accuracy: number | null;
  /** Whether the phrase actually served for this index came from BELOW its
   *  requested band (the backend's `complexity_below_band` fallback). Recorded
   *  at grade time; `null` until graded. A below-band Green is held rather than
   *  climbing the band — see {@link AssessmentStream.grade}. */
  belowBand: boolean | null;
  /**
   * Did the gen-phrase request for this phrase actually carry a complexity
   * filter?
   *
   * The band above is what the model PLANNED. It is always populated, because
   * the recurrence runs whether or not the session map is loaded. Whether that
   * plan reached the backend is a separate fact: `requestBand` is undefined
   * until the map loads, and the request then goes out with no
   * complexityMin/complexityMax.
   *
   * Recording only the plan made the two indistinguishable in telemetry — a row
   * showing the seed band 0.1-0.3 could have been requested unbanded and
   * {@link selfCheck} would still pass, because it diffs the plan against the
   * model and both agree. This field is what makes that visible.
   */
  banded: boolean;
  /**
   * The band as it actually went over the wire, in raw NWCS units [0-100].
   * `undefined` exactly when {@link banded} is false — there was no wire value.
   *
   * Kept alongside the normalized edges rather than instead of them because
   * neither substitutes for the other: the normalized pair is what a live
   * oracle comparison diffs, and the raw pair is the only scale joinable
   * against `complexity_characteristics.nwcs` on the backend.
   */
  requestBand: NwcsBand | undefined;
}

/**
 * One phrase's served band, flattened to the shape the analytics event carries.
 *
 * Snake_case because these go on the wire verbatim as columns of the
 * per-phrase completed event; they are not read anywhere in the client.
 *
 * This is the analytics half of {@link PhaseRecord}: the plan-time fields only.
 * The grade-time fields (verdict, accuracy, error tally) are deliberately
 * absent — the event already carries accuracy and full per-note error data, and
 * the verdict is derivable from them.
 */
export interface ServedBand {
  assessment_idx: number;
  /** Normalized [0-1] — the oracle's column C. */
  served_position: number;
  /** Normalized [0-1] band floor. */
  served_band_min: number;
  /** Normalized [0-1] band ceiling. */
  served_band_max: number;
  /** Raw NWCS [0-100] floor as requested; absent when the request was
   *  unbanded, because then there was no wire value. */
  served_band_min_nwcs: number | undefined;
  /** Raw NWCS [0-100] ceiling as requested. */
  served_band_max_nwcs: number | undefined;
  /** The PAD-delayed verdict that moved this band; `null` on seed rows. */
  served_driver_verdict: Verdict | null;
  served_cooldown: number;
  served_rests_before: number;
  /** Whether the request carried a complexity filter at all. The field the
   *  backend cannot reconstruct — see {@link PhaseRecord.banded}. */
  banded: boolean;
  /** Whether the phrase actually served fell outside the requested band — the
   *  backend's `complexity_fallback` flag (rungs 4-5 of the selection ladder).
   *  Optional: folded in only when the served phrase carried it, so a run with
   *  no fallbacks sends nothing and the backend reads absence as "no fallback". */
  complexity_fallback?: boolean;
  /** Direction of the fallback: `'up'` reached above the band (an honest
   *  probe), `'down'` served below it (the no-climb case). `null`/absent when
   *  the serve was in-band. */
  complexity_fallback_direction?: 'up' | 'down' | null;
}

/** A single field that diverged between the live run and the pure model. */
export interface SelfCheckMismatch {
  idx: number;
  field: 'position' | 'crMin' | 'crMax';
  live: number;
  model: number;
}

/** Everything {@link ComplexityTelemetry.selfCheck} can find. */
export interface SelfCheckResult {
  /** Rows where the live band diverged from a clean model replay. */
  mismatches: SelfCheckMismatch[];
  /** Assessment indices whose request went out with no complexity filter.
   *
   *  Deliberately separate from `mismatches`: these rows are NOT model
   *  divergences — the plan matches perfectly, it simply never reached the
   *  backend. Diffing bands can never surface them, so a self-check that only
   *  reported mismatches would call an unfiltered session clean. */
  unbanded: number[];
}

export class ComplexityTelemetry {
  private readonly records = new Map<number, PhaseRecord>();

  /** Record the band used to request an assessment phrase (plan time). */
  recordPlan(record: {
    idx: number;
    position: number;
    crMin: number;
    crMax: number;
    driver: Verdict | undefined;
    cooldown: number;
    restsBefore: number;
    /** The raw NWCS band the outgoing request carried, or `undefined` if it
     *  carried none. See {@link PhaseRecord.banded}. */
    requestBand: NwcsBand | undefined;
  }): void {
    if (this.records.has(record.idx)) return; // idempotent — first plan wins
    this.records.set(record.idx, {
      idx: record.idx,
      verdict: null,
      modelVerdict: null,
      errorTotal: null,
      redNotes: null,
      yellowNotes: null,
      position: record.position,
      crMin: record.crMin,
      crMax: record.crMax,
      driver: record.driver,
      cooldown: record.cooldown,
      restsBefore: record.restsBefore,
      accuracy: null,
      belowBand: null,
      banded: record.requestBand !== undefined,
      requestBand: record.requestBand,
    });
  }

  /** One phrase's record by assessment index, for callers that need the served
   *  band per phrase rather than the whole table. */
  row(idx: number): Readonly<PhaseRecord> | undefined {
    return this.records.get(idx);
  }

  /** Fill in the verdict once the phrase has been graded (completion time). */
  recordGrade(
    idx: number,
    grade: {
      verdict: Verdict;
      errorTotal: number;
      redNotes: number;
      yellowNotes: number;
      accuracy: number;
      /** Whether the served phrase fell below its requested band. Defaults to
       *  false so existing callers need no change. */
      belowBand?: boolean;
      /** The verdict actually fed to the recurrence (a held below-band Green is
       *  Yellow here). Defaults to `verdict`, so a caller that never holds is
       *  unchanged and `selfCheck` still diffs against the earned verdict. */
      modelVerdict?: Verdict;
    },
  ): void {
    const record = this.records.get(idx);
    if (record === undefined || record.verdict !== null) return;
    record.verdict = grade.verdict;
    record.modelVerdict = grade.modelVerdict ?? grade.verdict;
    record.errorTotal = grade.errorTotal;
    record.redNotes = grade.redNotes;
    record.yellowNotes = grade.yellowNotes;
    record.accuracy = grade.accuracy;
    record.belowBand = grade.belowBand ?? false;
  }

  /** All records in assessment order. */
  rows(): PhaseRecord[] {
    return Array.from(this.records.values()).sort((a, b) => a.idx - b.idx);
  }

  /** Just the graded verdict sequence, e.g. "GYRRG" — paste into the oracle's
   *  Status column (B). Ungraded trailing phrases are omitted. */
  verdictString(): string {
    return this.rows()
      .filter((r) => r.verdict !== null)
      .map((r) => r.verdict)
      .join('');
  }

  /**
   * Tab-separated table, header + one row per phrase, ready to paste into a
   * spreadsheet. Columns are ordered A/B/C/D-first to overlay the oracle.
   */
  toTsv(): string {
    const header = [
      'Phrase#',
      'Verdict',
      // Position is the oracle's column C. CR_Min/CR_Max follow so the sheet's
      // C/D still line up when the tier is floor-anchored, where C == Position.
      'Position',
      'CR_Min',
      'CR_Max',
      'ErrorTotal',
      'Red',
      'Yellow',
      'Driver',
      'Cooldown',
      'RestsBefore',
      'Accuracy',
      // Not an oracle column. Whether the request carried a filter at all —
      // without it a row planned at 0.1-0.3 looks identical whether or not the
      // backend ever saw those bounds.
      'Banded',
    ].join('\t');
    const rows = this.rows().map((r) =>
      [
        r.idx,
        r.verdict ?? '',
        r.position,
        r.crMin,
        r.crMax,
        r.errorTotal ?? '',
        r.redNotes ?? '',
        r.yellowNotes ?? '',
        r.driver ?? '',
        r.cooldown,
        r.restsBefore,
        r.accuracy ?? '',
        r.banded ? 'Y' : 'N',
      ].join('\t'),
    );
    return [header, ...rows].join('\n');
  }

  /**
   * Diff the live-recorded bands against a clean `computeTrajectory` of the same
   * verdicts, and separately report any phrase requested without a filter.
   *
   * Both empty means the runtime reproduced the model exactly (hence the
   * oracle) AND every request actually carried the band it planned. Either
   * populated is a runtime bug worth chasing — for different reasons, which is
   * why they are reported apart.
   */
  selfCheck(config: TierConfig): SelfCheckResult {
    const graded = this.rows().filter((r) => r.verdict !== null);
    // The live band was stepped by what the model CONSUMED — the held verdict,
    // which is Yellow for a below-band Green. Diffing against the earned verdict
    // instead would flag every held Green as a "mismatch" that is really the fix
    // working. `modelVerdict` falls back to `verdict` when nothing was held.
    const verdicts = graded.map((r) => (r.modelVerdict ?? r.verdict) as Verdict);
    const trajectory = computeTrajectory(verdicts, config, verdicts.length);
    const mismatches: SelfCheckMismatch[] = [];
    graded.forEach((row, i) => {
      const model = trajectory[i];
      if (Math.abs(row.position - model.position) > 1e-9) {
        mismatches.push({
          idx: row.idx,
          field: 'position',
          live: row.position,
          model: model.position,
        });
      }
      if (Math.abs(row.crMin - model.crMin) > 1e-9) {
        mismatches.push({ idx: row.idx, field: 'crMin', live: row.crMin, model: model.crMin });
      }
      if (Math.abs(row.crMax - model.crMax) > 1e-9) {
        mismatches.push({ idx: row.idx, field: 'crMax', live: row.crMax, model: model.crMax });
      }
    });
    // Over ALL rows, not just graded ones: the seed pair is the likeliest to go
    // out unbanded (it is planned during scene boot, before the session map can
    // answer) and may not be graded yet when a check is run mid-session.
    const unbanded = this.rows()
      .filter((r) => !r.banded)
      .map((r) => r.idx);
    return { mismatches, unbanded };
  }

  reset(): void {
    this.records.clear();
  }
}

/**
 * The oracle's parameter cells (column I) for a given tier config — paste these
 * into the sheet so its recurrence matches the run.
 *
 * ⚠️ Exact only at `cvr = 0.10`. The sheet derives its PLATEAU tolerance as
 * `CVR/2 + 0.01`; the runtime pins it at 0.06 (ruled #144 item 2), and the two
 * coincide only at the sheet's own width. Pasting the shipping `CVR = 0.20`
 * gives the sheet a 0.11 tolerance, so its rows will diverge from the app
 * wherever a step falls between the two — correctly, and not a runtime bug.
 */
export function oracleParams(config: TierConfig): Record<string, number> {
  return {
    complexity_start: config.complexityStart,
    CVR: config.cvr,
    CAI: config.cai,
    CDI: config.cdi,
    PAD: config.pad,
    AIM: config.aim,
    GIM: config.gim,
    CAT: config.cat,
  };
}

/** Live source of the current session's telemetry + config. Returns undefined
 *  when no complexity session is active (flag off, between sessions). */
export type FlowbotSource = () =>
  | { telemetry: ComplexityTelemetry; config: TierConfig }
  | undefined;

/** The console API attached to `window.flowbot`. */
export interface FlowbotConsole {
  rows(): PhaseRecord[];
  table(): void;
  tsv(): string;
  verdicts(): string;
  params(): Record<string, number>;
  check(): SelfCheckResult;
  copy(): Promise<void>;
}

/**
 * Install a `window.flowbot` console helper for oracle comparison. Idempotent —
 * always reads the *current* session via `source`, so it survives the
 * controller being rebuilt on each tier change. Testing aid only; safe to call
 * where there is no `window` (no-op).
 *
 *   flowbot.table()    // console.table of every graded phrase
 *   flowbot.tsv()      // paste-into-spreadsheet TSV (also logged)
 *   flowbot.verdicts() // "GYRRG…" for the oracle's Status column
 *   flowbot.params()   // parameter cells to match the oracle
 *   flowbot.check()    // diff live bands vs the pure model — [] means faithful
 *   flowbot.copy()     // copy the TSV to the clipboard
 */
export function installFlowbotConsole(source: FlowbotSource): void {
  if (typeof window === 'undefined') return;

  const active = (): { telemetry: ComplexityTelemetry; config: TierConfig } => {
    const current = source();
    if (current === undefined) {
      throw new Error('flowbot: no active complexity session (is the feature flag on?)');
    }
    return current;
  };

  const api: FlowbotConsole = {
    rows: () => active().telemetry.rows(),
    table: () => {
      console.table(active().telemetry.rows());
    },
    tsv: () => {
      const text = active().telemetry.toTsv();

      console.log(text);
      return text;
    },
    verdicts: () => active().telemetry.verdictString(),
    params: () => oracleParams(active().config),
    check: () => {
      const { telemetry, config } = active();
      const result = telemetry.selfCheck(config);

      if (result.mismatches.length === 0) {
        console.log('flowbot: live bands match the model exactly ✓');
      } else {
        console.log(result.mismatches);
      }
      // Reported separately and loudly: these rows are not model divergences,
      // so the line above says "✓" even when they exist. That is precisely the
      // blind spot — a band diff cannot see a request that carried no band.
      if (result.unbanded.length > 0) {
        console.warn(
          `flowbot: ${result.unbanded.length} phrase(s) requested with NO complexity filter ` +
            `(idx ${result.unbanded.join(', ')}) — planned band was never sent to gen-phrase`,
        );
      }
      return result;
    },
    copy: async () => {
      await navigator.clipboard.writeText(active().telemetry.toTsv());

      console.log('flowbot: TSV copied to clipboard');
    },
  };

  (window as unknown as { flowbot: FlowbotConsole }).flowbot = api;
}
